import { useBoAuth } from '@boContexts/BoAuthContext';
import { BO_AUTH_EXPIRED_STORAGE_KEY } from '@boServices/boAuthExpiryHandler';
import BoAuthService from '@boServices/boAuthService';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { faCheckCircle, faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { useGoogleLogin, useGoogleOneTapLogin } from '@react-oauth/google';
import Button from '@sharedComponents/Button';
import Card from '@sharedComponents/Card';
import Divider from '@sharedComponents/Divider';
import FlexBox from '@sharedComponents/FlexBox';
import ForgottenPassword from '@sharedComponents/ForgottenPassword';
import InformationMessage from '@sharedComponents/InformationMessage';
import InputText from '@sharedComponents/InputText';
import Loader from '@sharedComponents/Loader';
import LoginFooter from '@sharedComponents/LoginFooter';
import LoginHeader from '@sharedComponents/LoginHeader';
import SplitLayout from '@sharedComponents/SplitLayout';
import Typography from '@sharedComponents/Typography';
import { CONFIG_BO } from '@src/config';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

const boAuthService = new BoAuthService();
const hasGoogleClientId = !!CONFIG_BO.GOOGLE_CLIENT_ID;
// Commit SHA baked in at build time (see vite.config.bo.mjs) — shown in the footer to verify the
// deployed image matches the latest build.
const buildCommit = __BUILD_COMMIT__;

function GoogleAuthButton({ onCodeSuccess, onTapSuccess, onError }) {
    const { t } = useTranslation();

    const googleLogin = useGoogleLogin({
        flow: 'auth-code',
        onSuccess: onCodeSuccess,
        onError,
    });

    useGoogleOneTapLogin({
        onSuccess: onTapSuccess,
        onError,
        auto_select: true,
        cancel_on_tap_outside: true,
        use_fedcm_for_prompt: true,
    });

    return (
        <>
            <Button action={googleLogin} label={t('Sign in with Google')} icon={faGoogle} />
            <Divider text={t('OR')} />
        </>
    );
}

export default function BoLogin() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setAuthenticated } = useBoAuth();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(searchParams.get('error') === 'oauth' ? t('Google authentication failed') : null);
    const [displayedElement, setDisplayedElement] = useState('login');
    const [resetSuccess, setResetSuccess] = useState(false);
    const [sessionExpired, setSessionExpired] = useState(false);

    useEffect(() => {
        if (sessionStorage.getItem(BO_AUTH_EXPIRED_STORAGE_KEY)) {
            sessionStorage.removeItem(BO_AUTH_EXPIRED_STORAGE_KEY);
            setSessionExpired(true);
        }
        if (sessionStorage.getItem('passwordResetSuccess')) {
            sessionStorage.removeItem('passwordResetSuccess');
            setResetSuccess(true);
        }
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [displayedElement]);

    const handleGoogleCodeSuccess = async ({ code }) => {
        setLoading(true);
        setError(null);
        try {
            await boAuthService.postGoogleCode(code);
            await setAuthenticated();
            navigate('/');
        } catch (err) {
            setError(err?.status === 401 ? t('Access denied') : t('An error occurred, please try again'));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleOneTapSuccess = async (credentialResponse) => {
        setLoading(true);
        setError(null);
        try {
            await boAuthService.postGoogleCredential(credentialResponse.credential);
            await setAuthenticated();
            navigate('/');
        } catch (err) {
            setError(err?.status === 401 ? t('Access denied') : t('An error occurred, please try again'));
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordLogin = async () => {
        setLoading(true);
        setError(null);
        try {
            await boAuthService.loginWithPassword(email, password);
            await setAuthenticated();
            navigate('/');
        } catch (err) {
            setError(err?.status === 401 ? t('Invalid credentials') : t('An error occurred, please try again'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <SplitLayout imageType="LOGIN" backgroundColor="primary">
            <FlexBox flexDirection="column" gap="xl" additionnalStyles={{ width: '100%', maxWidth: '700px', margin: '0 auto' }}>
                <LoginHeader />
                {resetSuccess && (
                    <InformationMessage status="success" text={t('Your password has been successfully updated.')} icon={faCheckCircle} />
                )}
                {sessionExpired && (
                    <InformationMessage
                        status="warning"
                        text={t('Your session has expired. Please log in again.')}
                        icon={faExclamationCircle}
                    />
                )}
                {displayedElement === 'login' && (
                    <Card theme="login">
                        <FlexBox flexDirection="column" gap="xl" additionnalStyles={{ alignItems: 'center', padding: '24px' }}>
                            <Typography>{t('Connect to your admin space')}</Typography>
                            {loading ? (
                                <FlexBox flexDirection="column" gap="m" additionnalStyles={{ alignItems: 'center' }}>
                                    <Loader />
                                    <Typography>{t('Signing in...')}</Typography>
                                </FlexBox>
                            ) : (
                                <FlexBox flexDirection="column" gap="m" additionnalStyles={{ width: '100%', alignItems: 'center' }}>
                                    {hasGoogleClientId && (
                                        <GoogleAuthButton
                                            onCodeSuccess={handleGoogleCodeSuccess}
                                            onTapSuccess={handleGoogleOneTapSuccess}
                                            onError={() => setError(t('An error occurred, please try again'))}
                                        />
                                    )}
                                    <form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            handlePasswordLogin();
                                        }}
                                        style={{ width: '100%' }}
                                    >
                                        <FlexBox flexDirection="column" gap="m">
                                            <InputText
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder={t('Email')}
                                                required
                                            />
                                            <InputText
                                                type="password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                placeholder={t('Password')}
                                                required
                                            />
                                            <FlexBox justifyContent="center">
                                                <Button action={handlePasswordLogin} label={t('Sign in')} disabled={!email || !password} />
                                            </FlexBox>
                                        </FlexBox>
                                    </form>
                                    <Button ghost label={t('Forgotten password')} action={() => setDisplayedElement('forgotPassword')} />
                                </FlexBox>
                            )}
                            {error && <InformationMessage status="error" text={error} icon={faExclamationCircle} />}
                        </FlexBox>
                    </Card>
                )}
                {displayedElement === 'forgotPassword' && (
                    <ForgottenPassword
                        onSendEmail={(emailAddr) => boAuthService.requestPasswordReset(emailAddr)}
                        onBack={() => setDisplayedElement('login')}
                    />
                )}
                <LoginFooter version={buildCommit} />
            </FlexBox>
        </SplitLayout>
    );
}
